/**
 * Direct video files play in a <video> tag. Embed pages stay in an iframe.
 * mp4, m4v and webm count. avi does not.
 * A Drive file is stored as uc?id= and shown as /file/d/ID/preview: Drive
 * refuses a cross-site video tag.
 */

const DIRECT_EXT = /\.(mp4|m4v|webm)$/i;
const AVI_EXT = /\.avi$/i;

function bare(value) {
    return String(value || '').split(/[?#]/)[0];
}

function valueHasExt(value, pattern) {
    try {
        return pattern.test(decodeURIComponent(bare(value)));
    } catch (err) {
        return pattern.test(bare(value));
    }
}

/**
 * @param {string} raw
 * @returns {URL|null}
 */
function parseUrl(raw) {
    try {
        return new URL(String(raw || '').trim());
    } catch (err) {
        return null;
    }
}

/**
 * @param {URL} url
 * @param {RegExp} pattern
 * @returns {boolean}
 */
function urlHasExt(url, pattern) {
    if (valueHasExt(url.pathname, pattern)) {
        return true;
    }
    for (const value of url.searchParams.values()) {
        if (valueHasExt(value, pattern)) {
            return true;
        }
    }
    return false;
}

/**
 * Drive URL that already points at the file bytes, not a preview or view page.
 *
 * @param {string} raw
 * @returns {string|null} uc?id= URL
 */
export function drive_bytes_playback_url(raw) {
    const url = parseUrl(raw);
    if (!url) {
        return null;
    }
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    const id = url.searchParams.get('id');
    if (host === 'drive.usercontent.google.com') {
        if (id && url.pathname.includes('/download')) {
            return `https://drive.google.com/uc?id=${encodeURIComponent(id)}`;
        }
        return null;
    }
    if (host !== 'drive.google.com' && host !== 'docs.google.com') {
        return null;
    }
    if (/\/folders(?:\/|$)/.test(url.pathname) || /\/(preview|view)(?:\/|$)/.test(url.pathname)) {
        return null;
    }
    if ((url.pathname === '/uc' || url.pathname.endsWith('/uc')) && id) {
        return `https://drive.google.com/uc?id=${encodeURIComponent(id)}`;
    }
    return null;
}

/**
 * Any convertible Drive file link, including /open?id= and /file/d/ID/view.
 * Folders stay null. Used when saving a trailer, before it is stored.
 *
 * @param {string} raw
 * @returns {string|null}
 */
export function drive_file_playback_url(raw) {
    const bytes = drive_bytes_playback_url(raw);
    if (bytes) {
        return bytes;
    }
    const url = parseUrl(raw);
    if (!url) {
        return null;
    }
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    if (host !== 'drive.google.com' && host !== 'docs.google.com') {
        return null;
    }
    if (/\/folders(?:\/|$)/.test(url.pathname)) {
        return null;
    }
    const fromPath = url.pathname.match(/\/file\/d\/([^/]+)/);
    if (fromPath) {
        return `https://drive.google.com/uc?id=${encodeURIComponent(fromPath[1])}`;
    }
    if ((url.pathname === '/open' || url.pathname.endsWith('/open')) && url.searchParams.get('id')) {
        return `https://drive.google.com/uc?id=${encodeURIComponent(url.searchParams.get('id'))}`;
    }
    return null;
}

/**
 * Preview iframe for a Drive file. Folders stay null.
 * The stored URL remains uc?id=; this is display only.
 *
 * @param {string} raw
 * @returns {string|null}
 */
export function drive_embed_url(raw) {
    const url = parseUrl(raw);
    if (!url) {
        return null;
    }
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    const id = url.searchParams.get('id');
    if (host === 'drive.usercontent.google.com') {
        if (id && url.pathname.includes('/download')) {
            return drive_preview(id);
        }
        return null;
    }
    if (host !== 'drive.google.com' && host !== 'docs.google.com') {
        return null;
    }
    if (/\/folders(?:\/|$)/.test(url.pathname)) {
        return null;
    }
    const fromPath = url.pathname.match(/\/file\/d\/([^/]+)/);
    if (fromPath) {
        return drive_preview(fromPath[1]);
    }
    const isFileQuery = url.pathname === '/uc' || url.pathname.endsWith('/uc')
        || url.pathname === '/open' || url.pathname.endsWith('/open');
    if (isFileQuery && id) {
        return drive_preview(id);
    }
    return null;
}

function drive_preview(id) {
    return `https://drive.google.com/file/d/${encodeURIComponent(id)}/preview`;
}

/**
 * @param {string} raw
 * @returns {string|null} src for a video element, or null to use an iframe
 */
export function direct_video_src(raw) {
    const url = parseUrl(raw);
    if (!url) {
        return null;
    }
    if (urlHasExt(url, AVI_EXT)) {
        return null;
    }
    if (urlHasExt(url, DIRECT_EXT)) {
        return String(raw).trim();
    }
    return null;
}
